/**
 * /downhole/tickets/:id — one field ticket (0461). The customer's decision
 * email links here, so this page has to answer "what happened, and what do I
 * do next" on its own.
 *
 * WHAT IS OFFERED WHEN (the API's rules, so no button is dead):
 *   draft               Edit, Approve as office, Delete
 *   office_approved     Send / Resend, Record customer approval, PDF, Edit
 *                       (un-signs back to draft), Delete
 *   customer_rejected   the reason, Edit, Approve as office again, PDF, Delete
 *   customer_approved   who approved it and how, PDF, the stamped copy (an
 *                       office-recorded approval), Edit internal notes only
 * Writes are admin and office_staff; dispatch reads.
 *
 * STORED FIGURES ONLY. The subtotal, tax lines and total are the snapshot the
 * office approval signed, in the ticket's currency; a draft has none and shows
 * `—`, never 0. Nothing here adds money up.
 *
 * `sent_to` is answered by the send and nowhere else (the ticket read carries
 * the email's status, not the address), so the address is shown for the send
 * that just happened and the status after that.
 *
 * A DAILY TICKET (scope 'job_day', api#1062) bills one day of a Directional &
 * MWD well job, not an assembly's runs, and reads as a daily report: the well
 * job and work date, the day's kind and state, depths and footage, the crew
 * that day, the runs in the hole that day, and its lines. The ticket read
 * carries none of the day's facts, so they come from the API's own day price
 * (GET /jobs/:id/days/:date) and the job's crew (GET /jobs/:id); the browser
 * derives none of them: "crew that day" is exactly the spells the API's day
 * price bills. Runs are labelled from `runs[]` (BHA + run number), because run
 * numbers are per assembly. Assembly tickets are unchanged.
 */
import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { CalendarDays, CheckCircle2, CircleDollarSign, Download, FileSignature, FileText, Hash, Loader2, Mail, Pencil, Receipt, Send, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { api, getApiErrorMessage, getBlobApiErrorMessage } from '../../api/client';
import { PageHeaderBand } from '../../components/layout/PageHeaderBand';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';
import { useAuth } from '../../contexts/AuthContext';
import { displayValue } from '../../features/fieldiq-handoff/contracts';
import { useDeleteTicket, useDownholeAssemblyRuns, useDownholeTicket, useOfficeApproveTicket } from '../../hooks/useDownhole';
import { useDownholeJob, useJobDay } from '../../hooks/useDownholeJobs';
import { useTenantTimeZone } from '../../hooks/useTenantTimeZone';
import { formatCalendarDate, formatInstant, formatInstantDay } from '../../lib/calendar-date';
import { fmtCurrency, fmtQty } from '../../lib/display-labels';
import { DH_CREW_SHIFT_LABEL, DH_DAY_KIND_LABEL, DH_DAY_STATE_LABEL, DH_PERSONNEL_ROLE_LABEL, dhLabel } from '../../lib/downhole-labels';
import { formatDh } from '../../lib/downhole-units';
import { onlineOrSay } from '../../lib/downhole-offline';
import { queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import { isDailyTicket, type DhCrewSpell, type DhDayPrice, type DhTicketDetail, type DhUnitSystem } from '../../types/downhole';
import { DH_SECONDARY, DhDialog, DhEmpty, DhPanel, DhSpec, RunStatePill } from './DownholeBits';
import {
  RecordApprovalDialog,
  RefusalNote,
  SendTicketDialog,
  TicketFormDialog,
  TicketStatusBadge,
  apiErrorProblems,
  canReadDownholeTickets,
  canWriteDownholeTickets,
  ticketEmailLabel,
} from './ticketBits';

const BAND_SECONDARY =
  'inline-flex h-10 items-center gap-1.5 rounded-fi-control border border-white/25 px-4 text-sm font-bold transition hover:bg-white/10 disabled:opacity-50';
const PRIMARY = 'fi-action fi-action--standard fi-action--primary disabled:cursor-not-allowed disabled:opacity-50';
const DANGER =
  'inline-flex h-9 items-center gap-1.5 rounded-fi-control border border-black/20 bg-fi-signal-red px-3 text-sm font-bold text-fi-signal-ink transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50';
const SNAPSHOT = ['office_approved', 'customer_approved', 'customer_rejected'];

const TH = 'px-4 py-3 text-left text-[0.64rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted';
const TD = 'px-4 py-3 text-sm text-fieldiq-text';
const TD_NUM = 'whitespace-nowrap px-4 py-3 text-right font-mono text-sm tabular-nums text-fieldiq-text';

type Dialog = 'edit' | 'send' | 'record' | 'delete' | 'approve' | 'pdf' | null;

/** Fetch a Blob from the API into an object URL; the URL is revoked when replaced or unmounted. */
function useBlobUrl() {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => () => {
    if (url) URL.revokeObjectURL(url);
  }, [url]);
  return [url, setUrl] as const;
}

export default function TicketDetailScreen() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canRead = canReadDownholeTickets(user?.role);
  const canWrite = canWriteDownholeTickets(user?.role);
  const tz = useTenantTimeZone();
  const query = useDownholeTicket(canRead ? id : undefined);
  const failure = queryLoadFailure(query);
  const t = query.data?.data ?? null;
  const daily = isDailyTicket(t);
  const runsQuery = useDownholeAssemblyRuns(daily ? undefined : t?.assembly_id ?? undefined);
  // A daily ticket's facts live on its well job's day and crew, not on the ticket.
  const dayQuery = useJobDay(t?.dh_job_id ?? '', daily && t?.dh_job_id && t?.work_date ? t.work_date : null);
  const jobQuery = useDownholeJob(daily ? t?.dh_job_id ?? undefined : undefined, daily);
  const approve = useOfficeApproveTicket();
  const remove = useDeleteTicket();
  const [dialog, setDialog] = useState<Dialog>(null);
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [refusal, setRefusal] = useState<{ message: string; problems: string[] } | null>(null);
  const [pdfUrl, setPdfUrl] = useBlobUrl();
  const [pdfBusy, setPdfBusy] = useState(false);
  const loading = canRead && query.isLoading;

  const doApprove = async () => {
    if (!t || !onlineOrSay(toast.error)) return;
    setRefusal(null);
    try {
      await approve.mutateAsync(t.id);
      toast.success(`${t.ticket_number} approved by the office`);
      setDialog(null);
    } catch (error) {
      setDialog(null);
      // RUNS_UNPRICED carries the tools with no rate; TAX_UNCONFIGURED and the
      // rest are one sentence. Shown as the API wrote them, on the page.
      setRefusal({ message: getApiErrorMessage(error, 'The ticket was not approved.'), problems: apiErrorProblems(error) });
    }
  };

  const doDelete = async () => {
    if (!t || !onlineOrSay(toast.error)) return;
    try {
      await remove.mutateAsync(t.id);
      toast.success(`${t.ticket_number} deleted; its runs are free again`);
      navigate('/downhole/tickets');
    } catch (error) {
      setDialog(null);
      setRefusal({ message: getApiErrorMessage(error, 'The ticket was not deleted.'), problems: [] });
    }
  };

  const openPdf = async () => {
    if (!t || !onlineOrSay(toast.error)) return;
    setPdfBusy(true);
    try {
      const res = await api.downhole.tickets.pdf(t.id);
      setPdfUrl(URL.createObjectURL(res.data));
      setDialog('pdf');
    } catch (error) {
      toast.error(await getBlobApiErrorMessage(error, 'The ticket PDF could not be made.'));
    } finally {
      setPdfBusy(false);
    }
  };

  const downloadStamped = async () => {
    if (!t || !onlineOrSay(toast.error)) return;
    try {
      const res = await api.downhole.tickets.stampedFile(t.id);
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${t.ticket_number}-stamped`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 30_000);
    } catch (error) {
      toast.error(await getBlobApiErrorMessage(error, 'The stamped copy could not be opened.'));
    }
  };

  const status = t?.status;
  const hasSnapshot = !!status && SNAPSHOT.includes(status);
  const cur = t?.currency ?? null;

  const actions = t && (
    <div className="flex flex-wrap gap-2">
      {hasSnapshot && (
        <button type="button" className={BAND_SECONDARY} onClick={openPdf} disabled={pdfBusy}>
          {pdfBusy ? <Loader2 className="size-4 animate-spin" aria-hidden="true" /> : <FileText className="size-4" aria-hidden="true" />}
          PDF
        </button>
      )}
      {canWrite && (status === 'draft' || status === 'customer_rejected') && (
        <button type="button" className={PRIMARY} onClick={() => setDialog('approve')}>
          <CheckCircle2 className="size-4" aria-hidden="true" />
          {status === 'customer_rejected' ? 'Approve again as office' : 'Approve as office'}
        </button>
      )}
      {canWrite && status === 'office_approved' && (
        <button type="button" className={PRIMARY} onClick={() => setDialog('send')}>
          <Send className="size-4" aria-hidden="true" />
          {t.sent_to_customer_at ? 'Resend to customer' : 'Send to customer'}
        </button>
      )}
    </div>
  );

  return (
    <div data-testid="downhole-ticket-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow={t ? (daily ? `Daily report · ${t.job_number ?? '—'}` : `Field ticket · ${t.assembly_number ?? '—'}`) : 'Field ticket'}
        title={t ? t.ticket_number : loading ? 'Loading…' : 'Field ticket'}
        onBack={() => navigate(-1)}
        backLabel="Back"
        actions={actions || undefined}
        stats={
          !t
            ? undefined
            : daily
              ? [
                  // Month and day on the band (a year does not fit a phone's stat column); the full date is in the body.
                  { icon: <CalendarDays className="size-4" aria-hidden="true" />, label: 'Day', sub: 'Work date', tone: 'neutral', value: formatCalendarDate(t.work_date, { month: 'short', day: 'numeric' }) ?? '—' },
                  // run_numbers repeat across BHAs on a day, so the band carries the count only.
                  { icon: <Hash className="size-4" aria-hidden="true" />, label: 'Runs', sub: 'In the hole that day', tone: 'neutral', value: fmtQty(t.run_count) },
                  { icon: <CircleDollarSign className="size-4" aria-hidden="true" />, label: 'Total', sub: cur ?? 'Currency —', tone: 'accent', value: fmtCurrency(t.total_amount, cur) },
                  { icon: <Mail className="size-4" aria-hidden="true" />, label: 'Email', sub: t.sent_to_customer_at ? `Sent ${formatInstantDay(t.sent_to_customer_at, tz) ?? '—'}` : 'To the customer', tone: 'info', value: ticketEmailLabel(t.customer_email_status) },
                ]
              : [
                { icon: <Hash className="size-4" aria-hidden="true" />, label: 'Runs', sub: t.run_numbers.length ? `Run ${t.run_numbers.join(', ')}` : 'On the ticket', tone: 'neutral', value: fmtQty(t.run_count) },
                { icon: <Receipt className="size-4" aria-hidden="true" />, label: 'Subtotal', sub: hasSnapshot ? 'Before tax' : 'Priced at approval', tone: 'neutral', value: fmtCurrency(t.subtotal, cur) },
                { icon: <CircleDollarSign className="size-4" aria-hidden="true" />, label: 'Total', sub: cur ?? 'Currency —', tone: 'accent', value: fmtCurrency(t.total_amount, cur) },
                { icon: <Mail className="size-4" aria-hidden="true" />, label: 'Email', sub: t.sent_to_customer_at ? `Sent ${formatInstantDay(t.sent_to_customer_at, tz) ?? '—'}` : 'To the customer', tone: 'info', value: ticketEmailLabel(t.customer_email_status) },
              ]
        }
      />

      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          {!canRead ? (
            <div className="rounded-fi-panel border border-fieldiq-border bg-fieldiq-card">
              <DhEmpty>Field tickets carry prices, so only admin, office staff and dispatch can open them.</DhEmpty>
            </div>
          ) : loading ? (
            <div className="flex justify-center py-16">
              <Loader2 className="size-6 animate-spin text-fieldiq-muted" aria-label="Loading ticket" />
            </div>
          ) : !t ? (
            <div className="rounded-fi-panel border border-fieldiq-border bg-fieldiq-card">
              {failure ? (
                <LoadFailureNotice error={queryFailureError(query)} kind={failure} subject="this ticket" />
              ) : (
                <DhEmpty>This ticket does not exist, or it was deleted.</DhEmpty>
              )}
            </div>
          ) : (
            <TicketBody
              t={t}
              tz={tz}
              stale={!!failure}
              canWrite={canWrite}
              sentTo={sentTo}
              refusal={refusal}
              onEdit={() => setDialog('edit')}
              onRecord={() => setDialog('record')}
              onDelete={() => setDialog('delete')}
              onStamped={downloadStamped}
              day={
                daily
                  ? {
                      price: dayQuery.data?.data ?? null,
                      units: dayQuery.data?.meta?.unit_system ?? null,
                      failure: queryLoadFailure(dayQuery),
                      error: queryFailureError(dayQuery),
                      loading: dayQuery.isLoading,
                      crew: jobQuery.data?.data?.crew ?? null,
                    }
                  : null
              }
            />
          )}
        </div>
      </section>

      {t && canWrite && (
        <>
          <TicketFormDialog
            open={dialog === 'edit'}
            onClose={() => setDialog(null)}
            assemblyId={t.assembly_id}
            assemblyNumber={t.assembly_number}
            runs={runsQuery.data?.data ?? []}
            runsLoaded={!!runsQuery.data}
            ticket={t}
            tz={tz}
            daily={daily}
          />
          <SendTicketDialog open={dialog === 'send'} onClose={() => setDialog(null)} ticket={t} tz={tz} onSent={(to, cc) => setSentTo(cc.length ? `${to}, copied to ${cc.join(', ')}` : to)} />
          <RecordApprovalDialog open={dialog === 'record'} onClose={() => setDialog(null)} ticket={t} tz={tz} />
          <DhDialog
            open={dialog === 'approve'}
            title={`Approve ${t.ticket_number} as the office?`}
            onClose={() => setDialog(null)}
            footer={
              <>
                <button type="button" className={DH_SECONDARY} onClick={() => setDialog(null)}>
                  Cancel
                </button>
                <button type="button" className={PRIMARY} disabled={approve.isPending} onClick={doApprove}>
                  {approve.isPending && <Loader2 className="size-4 animate-spin" aria-hidden="true" />}
                  Approve
                </button>
              </>
            }
          >
            {daily ? (
              <p className="text-sm text-fieldiq-text">
                The day is priced from today&rsquo;s rate card (each crew member&rsquo;s day, each tool&rsquo;s day, footage and any
                mobilization) and the prices are fixed on this daily report. Those are the prices the customer approves and the invoice
                bills. It can then be sent to the customer.
              </p>
            ) : (
              <p className="text-sm text-fieldiq-text">
                Every run on it is priced from today&rsquo;s rate card and the prices are fixed on the ticket. Those are the prices the customer
                approves and the invoice bills. It can then be sent to the customer.
              </p>
            )}
          </DhDialog>
          <DhDialog
            open={dialog === 'delete'}
            title={`Delete ${t.ticket_number}?`}
            onClose={() => setDialog(null)}
            footer={
              <>
                <button type="button" className={DH_SECONDARY} onClick={() => setDialog(null)}>
                  Cancel
                </button>
                <button type="button" className={DANGER} disabled={remove.isPending} onClick={doDelete}>
                  {remove.isPending && <Loader2 className="size-4 animate-spin" aria-hidden="true" />}
                  Delete ticket
                </button>
              </>
            }
          >
            <p className="text-sm text-fieldiq-text">
              {daily
                ? `${formatCalendarDate(t.work_date) ?? 'The day'} goes back to having no ticket; it can be opened again from the well job.`
                : `Its ${t.run_count === 1 ? 'run goes' : `${t.run_count} runs go`} back to needing a ticket.`}
              {t.sent_to_customer_at ? ' The review link the customer was sent stops working.' : ''}
            </p>
          </DhDialog>
        </>
      )}
      {t && (
        <DhDialog
          open={dialog === 'pdf' && !!pdfUrl}
          wide
          title={`${t.ticket_number} · PDF`}
          onClose={() => setDialog(null)}
          footer={
            <>
              {pdfUrl && (
                <a className={DH_SECONDARY} href={pdfUrl} download={`ticket-${t.ticket_number}.pdf`}>
                  <Download className="size-4" aria-hidden="true" />
                  Download
                </a>
              )}
              <button type="button" className={DH_SECONDARY} onClick={() => setDialog(null)}>
                Close
              </button>
            </>
          }
        >
          {pdfUrl && <iframe title={`${t.ticket_number} PDF`} src={pdfUrl} className="h-[70vh] w-full rounded-fi-control border border-fieldiq-border bg-white" />}
        </DhDialog>
      )}
    </div>
  );
}

function TicketBody({
  t,
  tz,
  stale,
  canWrite,
  sentTo,
  refusal,
  onEdit,
  onRecord,
  onDelete,
  onStamped,
  day,
}: {
  t: DhTicketDetail;
  tz: string | null;
  stale: boolean;
  canWrite: boolean;
  sentTo: string | null;
  refusal: { message: string; problems: string[] } | null;
  onEdit: () => void;
  onRecord: () => void;
  onDelete: () => void;
  onStamped: () => void;
  /** A daily ticket's day, read from its well job. null for a per-assembly ticket. */
  day: DailyFacts | null;
}) {
  const cur = t.currency;
  const daily = !!day;
  const statusLine: Record<string, string> = {
    draft: 'A draft. The office approves it, which fixes the prices, before it can go to the customer.',
    office_approved: t.sent_to_customer_at
      ? `Sent to the customer ${formatInstant(t.sent_to_customer_at, tz) ?? ''}. Waiting for them to approve or reject it.`.replace('  ', ' ')
      : 'Approved by the office. Send it to the customer, or record their approval if they stamped a copy.',
    customer_approved: daily ? 'Approved by the customer. The day can be invoiced.' : 'Approved by the customer. Its runs can be invoiced.',
    customer_rejected: 'Rejected by the customer. Correct it, approve it again as the office, and resend.',
  };
  return (
    <>
      {stale && (
        <p role="status" className="text-xs text-fieldiq-muted">
          This ticket could not be refreshed just now, so it shows what loaded earlier.
        </p>
      )}
      <RefusalNote refusal={refusal} />
      <div className="flex flex-wrap items-center gap-3 rounded-fi-panel border border-fieldiq-border bg-fieldiq-card px-4 py-3">
        <TicketStatusBadge status={t.status} />
        <p className="min-w-0 flex-1 basis-56 text-sm text-fieldiq-text">{statusLine[t.status] ?? '—'}</p>
        {/* In the body, not on the band: a third band action wraps the title and
            moves the stat strip under the hero circle (band-clearance notes). */}
        {canWrite && t.status === 'office_approved' && (
          <button type="button" className={DH_SECONDARY} onClick={onRecord}>
            <FileSignature className="size-4" aria-hidden="true" />
            Record customer approval
          </button>
        )}
        {canWrite && (
          <button type="button" className={DH_SECONDARY} onClick={onEdit}>
            <Pencil className="size-4" aria-hidden="true" />
            {t.status === 'customer_approved' ? 'Edit notes' : 'Edit'}
          </button>
        )}
        {canWrite && t.status !== 'customer_approved' && (
          <button type="button" className={DH_SECONDARY} onClick={onDelete}>
            <Trash2 className="size-4" aria-hidden="true" />
            Delete
          </button>
        )}
      </div>

      {sentTo && (
        <div role="status" className="rounded-fi-panel border border-fieldiq-border border-l-4 border-l-fi-blue bg-fieldiq-card px-4 py-3 text-sm text-fieldiq-text">
          Sent to <span className="font-bold">{sentTo}</span>.
        </div>
      )}

      {t.status === 'customer_rejected' && (
        <div data-testid="ticket-rejection" className="rounded-fi-panel border border-fieldiq-border border-l-4 border-l-fi-red bg-fieldiq-card px-4 py-3 text-sm text-fieldiq-text">
          <p className="font-bold">The customer rejected this ticket{t.customer_approved_by_name ? ` (${t.customer_approved_by_name})` : ''}.</p>
          <p className="mt-1 whitespace-pre-line">{t.customer_rejection_reason?.trim() ? `“${t.customer_rejection_reason.trim()}”` : 'They gave no reason.'}</p>
        </div>
      )}

      {t.status === 'customer_approved' && (
        <div className="flex flex-wrap items-center gap-3 rounded-fi-panel border border-fieldiq-border border-l-4 border-l-fi-green bg-fieldiq-card px-4 py-3 text-sm text-fieldiq-text">
          <p className="min-w-0 flex-1 basis-64">
            Approved by <span className="font-bold">{t.customer_approved_by_name || '—'}</span> on {formatInstantDay(t.customer_approved_at, tz) ?? '—'}
            {t.customer_approval_source === 'office_upload'
              ? ', recorded by the office from a stamped copy.'
              : t.customer_approval_source === 'link'
                ? ', on the review link.'
                : '.'}
          </p>
          {t.customer_approval_source === 'office_upload' && (
            <button type="button" className={DH_SECONDARY} onClick={onStamped}>
              <Download className="size-4" aria-hidden="true" />
              Stamped copy
            </button>
          )}
          <Link to="/downhole/invoices" className="font-bold text-fieldiq-accent-signal underline">
            {daily ? 'Go to invoices' : 'Invoice its runs'}
          </Link>
        </div>
      )}

      {day && <DayPanel t={t} day={day} />}

      <DhPanel eyebrow={daily ? 'Daily report' : 'Ticket'} title="Details">
        <dl className="grid grid-cols-2 gap-x-6 gap-y-4 p-4 md:grid-cols-4">
          <DhSpec label="Customer" value={displayValue(t.customer_name)} />
          {daily ? (
            <DhSpec
              label="Well job"
              value={
                t.dh_job_id ? (
                  <Link to={`/downhole/wells/${t.dh_job_id}`} className="font-mono text-fieldiq-accent-signal hover:underline">
                    {t.job_number || '—'}
                  </Link>
                ) : (
                  '—'
                )
              }
            />
          ) : (
            <DhSpec
              label="Assembly"
              value={
                <Link to={`/downhole/assemblies/${t.assembly_id}/build`} className="font-mono text-fieldiq-accent-signal hover:underline">
                  {t.assembly_number || '—'}
                </Link>
              }
            />
          )}
          <DhSpec label="Well / Rig" value={t.well_name || t.rig_name ? `${t.well_name || '—'} / ${t.rig_name || '—'}` : '—'} />
          <DhSpec label="Province / state" value={displayValue(t.province_state)} />
          <DhSpec label="LSD / location" value={displayValue(t.lsd)} />
          <DhSpec label="AFE" value={displayValue(t.afe_number)} />
          <DhSpec label="PO" value={displayValue(t.po_number)} />
          <DhSpec label="Created" value={formatInstant(t.created_at, tz) ?? '—'} />
          <DhSpec label="Office approved" value={formatInstant(t.office_approved_at, tz) ?? '—'} />
          <DhSpec label="Sent to customer" value={formatInstant(t.sent_to_customer_at, tz) ?? '—'} />
          <DhSpec label="Customer email" value={ticketEmailLabel(t.customer_email_status)} />
          {/* The API stores the address as the send starts, so a failed or
              bounced email still has one: say so beside it, never just "sent". */}
          <DhSpec
            label="Last addressed to"
            value={
              t.customer_email_sent_to
                ? `${t.customer_email_sent_to}${t.customer_email_cc?.length ? ` (cc ${t.customer_email_cc.join(', ')})` : ''}${
                    t.customer_email_status === 'failed' || t.customer_email_status === 'bounced' || t.customer_email_status === 'complained'
                      ? ` — ${ticketEmailLabel(t.customer_email_status).toLowerCase()}`
                      : ''
                  }`
                : '—'
            }
          />
          <DhSpec label="Invoiced runs" value={fmtQty(t.invoiced_run_count)} />
        </dl>
        {t.customer_notes && (
          <p className="whitespace-pre-line border-t border-fieldiq-border px-4 py-3 text-sm text-fieldiq-text">
            <span className="block text-[0.62rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">For the customer</span>
            {t.customer_notes}
          </p>
        )}
        {t.notes && (
          <p className="whitespace-pre-line border-t border-fieldiq-border px-4 py-3 text-sm text-fieldiq-text">
            <span className="block text-[0.62rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">Internal notes</span>
            {t.notes}
          </p>
        )}
      </DhPanel>

      {day && <CrewThatDay day={day} tz={tz} />}

      <DhPanel eyebrow="Work" title={daily ? 'Runs that day' : 'Runs on this ticket'} testId="ticket-runs">
        {t.runs.length === 0 ? (
          <DhEmpty>{daily ? 'No run was in the hole that day.' : 'This ticket has no runs on it.'}</DhEmpty>
        ) : (
          <ul className="divide-y divide-fieldiq-border">
            {t.runs.map((r) => (
              <li key={r.id} className="flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-3 text-sm">
                <Link to={`/downhole/run-logs/${r.id}`} className="font-mono font-extrabold text-fieldiq-accent-signal hover:underline">
                  {/* Run numbers are per assembly: a day can hold run 1 of two BHAs. */}
                  {daily ? `${r.assembly_number ?? '—'} · Run ${r.run_number ?? '—'}` : `Run ${r.run_number ?? '—'}`}
                </Link>
                <RunStatePill state={r.run_state} />
                <span className="text-fieldiq-text">In {formatInstantDay(r.run_in_at, tz) ?? '—'}</span>
                <span className="text-fieldiq-text">Out {formatInstantDay(r.run_out_at, tz) ?? '—'}</span>
                <span className="text-fieldiq-text">{[r.well_name, r.rig_name].filter(Boolean).join(' · ') || '—'}</span>
                {r.invoice_id && (
                  <Link to={`/downhole/invoices/${r.invoice_id}`} className="text-fieldiq-accent-signal hover:underline">
                    {r.invoice_number || 'Invoice'}
                  </Link>
                )}
              </li>
            ))}
          </ul>
        )}
      </DhPanel>

      <DhPanel
        eyebrow="Prices the office signed"
        title="Line items"
        testId="downhole-ticket-lines"
        footer={t.line_items.length ? `${t.line_items.length} lines, in ${cur ?? '—'}` : undefined}
      >
        {t.line_items.length === 0 ? (
          <DhEmpty>
            {t.status === 'draft'
              ? daily
                ? "A draft carries no prices. They are fixed when the office approves it; until then the well job's day shows the live price."
                : 'A draft carries no prices. They are fixed when the office approves it.'
              : 'This ticket has no line items.'}
          </DhEmpty>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[820px]">
              <thead className="border-b border-fieldiq-border bg-fieldiq-dark/40">
                <tr>
                  {!daily && <th scope="col" className={TH}>Run</th>}
                  <th scope="col" className={TH}>Description</th>
                  <th scope="col" className={TH}>{daily ? 'Crew role / tool' : 'Component'}</th>
                  <th scope="col" className={`${TH} text-right`}>Qty</th>
                  <th scope="col" className={TH}>Unit</th>
                  <th scope="col" className={`${TH} text-right`}>Rate</th>
                  <th scope="col" className={`${TH} text-right`}>Amount</th>
                </tr>
              </thead>
              <tbody>
                {t.line_items.map((l) => (
                  <tr key={l.id} className="border-t border-fieldiq-border">
                    {!daily && <td className={`${TD} font-mono`}>{l.run_number ?? '—'}</td>}
                    <td className={`${TD} max-w-[28rem]`}>
                      {displayValue(l.description)}
                      {l.minimum_applied ? <span className="ml-2 text-xs text-fieldiq-muted">(minimum charge)</span> : null}
                    </td>
                    <td className={`${TD} ${l.personnel_role ? '' : 'font-mono'}`}>
                      {daily && l.personnel_role ? dhLabel(DH_PERSONNEL_ROLE_LABEL, l.personnel_role) : l.serial_number || '—'}
                    </td>
                    <td className={TD_NUM}>{fmtQty(l.quantity)}</td>
                    <td className={TD}>{displayValue(l.unit)}</td>
                    <td className={TD_NUM}>{fmtCurrency(l.rate, cur)}</td>
                    <td className={`${TD_NUM} font-extrabold text-white`}>{fmtCurrency(l.amount, cur)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <dl className="ml-auto w-full max-w-sm space-y-1 border-t border-fieldiq-border px-4 py-3 text-sm">
          <div className="flex justify-between">
            <dt className="text-fieldiq-muted">Subtotal</dt>
            <dd className="font-mono text-fieldiq-text">{fmtCurrency(t.subtotal, cur)}</dd>
          </div>
          {[
            [t.tax_rate_1_label, t.tax_rate_1_amount],
            [t.tax_rate_2_label, t.tax_rate_2_amount],
          ]
            .filter(([label]) => !!label)
            .map(([label, amount]) => (
              <div key={String(label)} className="flex justify-between">
                <dt className="text-fieldiq-muted">{String(label)}</dt>
                <dd className="font-mono text-fieldiq-text">{fmtCurrency(amount as number | null, cur)}</dd>
              </div>
            ))}
          <div className="flex justify-between border-t border-fieldiq-border pt-1 font-extrabold">
            <dt className="text-white">Total</dt>
            <dd className="font-mono text-white">{fmtCurrency(t.total_amount, cur)}</dd>
          </div>
        </dl>
      </DhPanel>
    </>
  );
}

/** What a daily ticket shows about its day: the API's day price and the job's crew. */
interface DailyFacts {
  price: DhDayPrice | null;
  units: DhUnitSystem | null;
  failure: ReturnType<typeof queryLoadFailure>;
  error: unknown;
  loading: boolean;
  /** The job's crew spells (GET /jobs/:id); null until read. */
  crew: DhCrewSpell[] | null;
}

const SPELL_FORMAT: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true };

/** The day as the API prices it: kind, state, depths, footage. Nothing derived here. */
function DayPanel({ t, day }: { t: DhTicketDetail; day: DailyFacts }) {
  const p = day.price;
  return (
    <DhPanel eyebrow="Daily report" title="The day" testId="ticket-day">
      {day.loading ? (
        <div className="flex justify-center py-8">
          <Loader2 className="size-5 animate-spin text-fieldiq-muted" aria-label="Loading the day" />
        </div>
      ) : day.failure || !p ? (
        <LoadFailureNotice error={day.error} kind={day.failure ?? 'unreachable'} subject="this day" />
      ) : (
        <dl className="grid grid-cols-2 gap-x-6 gap-y-4 p-4 md:grid-cols-4">
          <DhSpec label="Work date" value={formatCalendarDate(t.work_date, { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' }) ?? '—'} />
          <DhSpec label="Day" value={dhLabel(DH_DAY_KIND_LABEL, p.day_kind)} />
          <DhSpec
            label="State"
            value={p.state ? `${dhLabel(DH_DAY_STATE_LABEL, p.state)}${p.state_overridden ? ' (set by office)' : ''}` : '—'}
          />
          <DhSpec label="Footage" value={p.footage === null || p.footage === undefined ? '—' : `${fmtQty(p.footage)} ft`} />
          <DhSpec label="Depth at start" value={formatDh(p.depth_start_ft, 'length', day.units, { digits: 0 })} />
          <DhSpec label="Depth at end" value={formatDh(p.depth_end_ft, 'length', day.units, { digits: 0 })} />
          {p.state_overridden && p.override_reason && <DhSpec label="Why" value={p.override_reason} />}
        </dl>
      )}
    </DhPanel>
  );
}

/**
 * Crew that day: exactly the spells the API's day price bills (its lines carry
 * dh_job_crew_id), shown from the job's crew. No date arithmetic here: which
 * spells touch the day is the API's answer, not the browser's.
 */
function CrewThatDay({ day, tz }: { day: DailyFacts; tz: string | null }) {
  const ids: string[] = [];
  for (const l of day.price?.lines ?? []) if (l.dh_job_crew_id && !ids.includes(l.dh_job_crew_id)) ids.push(l.dh_job_crew_id);
  const byId = new Map((day.crew ?? []).map((w) => [w.id, w]));
  return (
    <DhPanel eyebrow="People" title="Crew that day" testId="ticket-crew">
      {day.loading ? (
        <div className="flex justify-center py-8">
          <Loader2 className="size-5 animate-spin text-fieldiq-muted" aria-label="Loading the crew" />
        </div>
      ) : day.failure || !day.price ? (
        <LoadFailureNotice error={day.error} kind={day.failure ?? 'unreachable'} subject="the crew that day" />
      ) : ids.length === 0 ? (
        <DhEmpty>No one was on the well that day.</DhEmpty>
      ) : (
        <ul className="divide-y divide-fieldiq-border">
          {ids.map((id) => {
            const w = byId.get(id);
            return (
              <li key={id} className="flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-3 text-sm" data-testid={`ticket-crew-${id}`}>
                <span className="font-bold text-white">{displayValue(w?.person_name)}</span>
                <span className="text-fieldiq-text">{w ? dhLabel(DH_PERSONNEL_ROLE_LABEL, w.personnel_role) : '—'}</span>
                <span className="text-fieldiq-text">{w ? dhLabel(DH_CREW_SHIFT_LABEL, w.shift) : '—'}</span>
                <span className="text-fieldiq-text">On {w ? formatInstant(w.on_at, tz, SPELL_FORMAT) ?? '—' : '—'}</span>
                <span className="text-fieldiq-text">{w ? (w.off_at ? `Off ${formatInstant(w.off_at, tz, SPELL_FORMAT) ?? '—'}` : 'Still on') : '—'}</span>
              </li>
            );
          })}
        </ul>
      )}
    </DhPanel>
  );
}
